import React, { useState, useEffect } from "react";
import Select from "react-select";
import { createDrawingSheet } from "../../services/operations/drawingsheetAPI";
import { uploadPicture } from "../../services/operations/SettingsAPI";
import { createEmployeeInvite, getInvitedEmployees } from "../../services/operations/adminAPI";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { PlusCircle, Users, Upload, FileText, Sparkles, Loader2 } from "lucide-react";

const CreateTeam = () => {
  const navigate = useNavigate();
  const [drawingNumber, setDrawingNumber] = useState("");
  const [employees, setEmployees] = useState([]);
  const [chairman, setChairman] = useState(null);
  const [scribe, setScribe] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState(null);
  const [imageUrl, setImageUrl] = useState("");

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await getInvitedEmployees();
        if (response.success) {
          const employeeOptions = response.data.map((emp) => ({
            value: emp.email,
            label: `${emp.firstName} ${emp.lastName} (${emp.email})`,
          }));
          setEmployees(employeeOptions);
        } else {
          toast.error("Failed to fetch employees.");
        }
      } catch (error) {
        console.error("Error fetching employees:", error);
        toast.error("Error fetching employees.");
      }
    };
    fetchEmployees();
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setImage(file);
  };

  const uploadImage = async () => {
    if (!image) return null;
    try {
      const formData = new FormData();
      formData.append("displayPicture", image);
      
      const response = await uploadPicture(formData);
      if (response && response.success) {
        setImageUrl(response.data.url);
        return response.data.url;
      } else {
        toast.error("Failed to upload image.");
        return null;
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Image upload failed.");
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!drawingNumber || !chairman || !scribe || teamMembers.length === 0) {
      return toast.error("Please fill all required fields.");
    }
    setLoading(true);
    try {
      const uploadedImageUrl = await uploadImage();
      if (!uploadedImageUrl) return setLoading(false);
  
      const teamEmails = teamMembers.map(member => member.value);
  
      const response = await createDrawingSheet({
        drawingNumber,
        chairman: chairman.value,
        scribe: scribe.value,
        teamEmails,
        drawingImage: uploadedImageUrl,
      });
  
      if (!response.success) {
        toast.error("Failed to create Drawing Sheet.");
        return setLoading(false);
      }
  
      toast.success("Team created successfully! Emails are being sent.");
      navigate("/dashboard/admin");
  
    } catch (err) {
      console.error("Error creating team:", err);
      toast.error("Failed to create team.");
    } finally {
      setLoading(false);
    }
  };
  

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
      backdropFilter: 'blur(10px)',
      borderColor: state.isFocused ? '#3B82F6' : 'rgba(255, 255, 255, 0.3)',
      borderWidth: '1px',
      borderRadius: '12px',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      padding: '4px',
      transition: 'all 0.3s ease',
      '&:hover': {
        borderColor: '#3B82F6',
        boxShadow: '0 8px 25px -5px rgba(0, 0, 0, 0.1)'
      }
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#3B82F6' : state.isFocused ? '#EFF6FF' : 'white',
      color: state.isSelected ? 'white' : '#374151',
      padding: '12px 16px',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: '#EFF6FF',
      borderRadius: '8px',
      border: '1px solid #DBEAFE'
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: '#3B82F6',
      fontWeight: '500'
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: '#6B7280',
      '&:hover': {
        backgroundColor: '#FEE2E2',
        color: '#DC2626'
      }
    })
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Modern Header */}
        <div className="text-center mb-6">
          <div className="flex justify-center items-center gap-2">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-xl">
              <Users className="h-6 w-6 text-white" />
            </div>
          
          <h1 className="text-3xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent ">
            Start New Hazop
          </h1>
          </div>
          <p className="text-slate-600 text-lg">Build your HAZOP analysis team</p>
        </div>

        {/* Form Container */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-white/50 to-blue-50/50 rounded-3xl"></div>
          <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/20 p-8">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Drawing Number */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-slate-700 font-semibold text-lg">
                  <FileText className="h-5 w-5 text-blue-500" />
                  Drawing Number
                </label>
                <input 
                  type="text" 
                  value={drawingNumber} 
                  onChange={(e) => setDrawingNumber(e.target.value)}
                  className="w-full bg-white/80 backdrop-blur-sm border border-white/30 rounded-xl p-4 text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 shadow-lg transition-all duration-300 hover:shadow-xl"
                  placeholder="Enter Drawing Number" 
                />
              </div>

              {/* Chairman Selection */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-slate-700 font-semibold text-lg">
                  <Users className="h-5 w-5 text-emerald-500" />
                  Select Chairman
                </label>
                <Select 
                  options={employees} 
                  value={chairman} 
                  onChange={setChairman} 
                  styles={customSelectStyles}
                  placeholder="Select Chairman" 
                  isSearchable
                />
              </div>

              {/* Scribe Selection */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-slate-700 font-semibold text-lg">
                  <Users className="h-5 w-5 text-indigo-500" />
                  Select Scribe
                </label>
                <Select 
                  options={employees} 
                  value={scribe} 
                  onChange={setScribe} 
                  styles={customSelectStyles}
                  placeholder="Select Scribe" 
                  isSearchable
                />
              </div>

              {/* Team Members Selection */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-slate-700 font-semibold text-lg">
                  <Users className="h-5 w-5 text-purple-500" />
                  Select Team Members
                </label>
                <Select 
                  options={employees} 
                  value={teamMembers} 
                  onChange={setTeamMembers} 
                  isMulti 
                  styles={customSelectStyles}
                  placeholder="Select Team Members" 
                  isSearchable
                />
              </div>

              {/* Image Upload */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-slate-700 font-semibold text-lg">
                  <Upload className="h-5 w-5 text-amber-500" />
                  Upload Drawing Image
                </label>
                <div className="relative">
                  <input 
                    type="file" 
                    accept="image/*" 
                    onChange={handleImageChange} 
                    className="w-full bg-white/80 backdrop-blur-sm border border-white/30 rounded-xl p-4 text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 shadow-lg transition-all duration-300 hover:shadow-xl file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-gradient-to-r file:from-blue-500 file:to-indigo-600 file:text-white file:font-medium file:cursor-pointer"
                  />
                  {image && (
                    <div className="mt-4 p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                      <p className="text-emerald-700 font-medium flex items-center gap-2">
                        <Sparkles className="h-4 w-4" />
                        Image selected: {image.name}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <button 
                type="submit"
                disabled={loading} 
                className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 disabled:from-slate-400 disabled:to-slate-500 text-white py-4 px-8 rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02] disabled:scale-100 disabled:cursor-not-allowed flex items-center justify-center gap-3"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin h-6 w-6" />
                    Creating Team...
                  </>
                ) : (
                  <>
                    <PlusCircle className="h-6 w-6" />
                    Create Team
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateTeam;